import React, { useEffect, useRef, useState } from 'react';
import { TabDefinition } from '../types';
import './TabNavigation.css';

interface TabNavigationProps {
  tabs: TabDefinition[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  rightAction?: {
    label: string | React.ReactNode;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  /** Extra content rendered INSIDE the scrollable strip, after the last tab
   *  (e.g. the Privacy trigger). Scrolls with the tabs — never pinned, so it
   *  can't shrink the touch-scroll area on mobile. */
  trailingContent?: React.ReactNode;
}

export const TabNavigation: React.FC<TabNavigationProps> = ({
  tabs,
  activeTab,
  onTabChange,
  rightAction,
  trailingContent
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(el.scrollLeft < maxScrollLeft - 2);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });

    // Resize can change overflow state
    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [tabs.length]);

  return (
    <div
      className={[
        'tab-navigation',
        canScrollLeft ? 'tab-navigation--can-scroll-left' : '',
        canScrollRight ? 'tab-navigation--can-scroll-right' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="tab-navigation-left" ref={scrollRef}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        {trailingContent}
      </div>

      {/* Edge affordances (purely visual) */}
      <div className="tab-scroll-hint tab-scroll-hint--left" aria-hidden="true" />
      <div className="tab-scroll-hint tab-scroll-hint--right" aria-hidden="true" />
      
      {rightAction && (
        <div className="tab-navigation-right">
          <button
            className="tab-action-button"
            onClick={rightAction.onClick}
            title={typeof rightAction.label === 'string' ? rightAction.label : undefined}
          >
            {rightAction.icon && <span className="tab-action-icon">{rightAction.icon}</span>}
            {rightAction.label}
          </button>
        </div>
      )}
    </div>
  );
};







